import merge from 'lodash/merge.js';
import orderBy from 'lodash/orderBy.js';

import axios from '@/plugins/axios.js';
import CourseAnnouncement from '@/store/modules/courseannouncements/CourseAnnouncement.js';
import Collection from '@/store/quill/Collection.js';
import { promiseError, toggleLoading } from '@/store/utilities/Helpers.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default merge(Collection('CourseAnnouncements', CourseAnnouncement), {
    state: {},

    getters: {
        sorted(state, getters) {
            return orderBy(getters.all(), 'created_at', 'desc');
        },
    },

    actions: {
        async fetch(context, { courseId }) {
            toggleLoading(context);

            const queryBuilder = new QueryBuilder(`courses/${courseId}/announcements`);
            queryBuilder.include(['creator']);

            try {
                const { data } = await axios.get(queryBuilder.toUrl());

                toggleLoading(context);

                return Promise.all([
                    context.dispatch('sort', { created_at: 'desc' }),
                    context.dispatch('updatePageMeta', data.meta),
                    context.dispatch('hydrate', data.data),
                ]).then(() => data.data);
            } catch (error) {
                toggleLoading(context);

                promiseError({
                    key: 'course_announcements_load_error',
                    context,
                });

                return error;
            }
        },

        async create(context, payload) {
            toggleLoading(context);

            const queryBuilder = new QueryBuilder(`courses/${payload.courseId}/announcements`);

            try {
                const { data } = await axios.post(queryBuilder.toUrl(), payload);

                context.dispatch('add', data.data);

                return data.data;
            } catch (error) {
                toggleLoading(context);

                promiseError({
                    key: 'course_announcements_create_error',
                    context,
                });

                return error;
            }
        },

        async updateAnnouncement(context, { announcement, important }) {
            toggleLoading(context);

            const queryBuilder = new QueryBuilder(`courses/${announcement.course_id}/announcements/${announcement.id}`);

            try {
                const { data } = await axios.put(
                    queryBuilder.toUrl(),
                    { important },
                    {
                        errorHandle: false,
                    }
                );

                // The update action expects the full data object, not a wrapper
                context.dispatch('update', data.data);

                return data.data;
            } catch (error) {
                toggleLoading(context);

                promiseError({
                    key: 'course_announcements_update_error',
                    context,
                });

                throw error;
            }
        },

        async deleteAnnouncement(context, post) {
            toggleLoading(context);

            const queryBuilder = new QueryBuilder(`courses/${post.course_id}/announcements/${post.id}`);

            try {
                const { data } = await axios.delete(queryBuilder.toUrl(), {
                    errorHandle: false,
                });

                await context.dispatch('remove', post.id);

                return data.data;
            } catch (error) {
                toggleLoading(context);

                promiseError({
                    key: 'course_announcements_delete_error',
                    context,
                });

                throw error;
            }
        },
    },

    mutations: {},
});
